import React, { useCallback, useEffect, useMemo } from 'react';

import { ExternalLinkIcon, InfoIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useTitle } from 'react-use';

import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import IntegrationsHeader from '@/components/organisms/Headers/IntegrationsHeader';
import { getIntegrationByType, integrationByType } from '@/components/organisms/Integrations';
import PageContentWrapper from '@/components/templates/PageContentWrapper';
import { useDeskId } from '@/hooks/useDeskId';
import useDesks from '@/hooks/useDesks';
import { useIntegrations } from '@/hooks/useIntegrations';
import {
  selectAvailableChannels,
  selectCategorizedIntegrations,
} from '@/redux/integrations/selectors';
import { getPermissions } from '@/redux/permissions/selectors';
import useSegments from '@/segment/useSegments';
import { Integration, IntegrationType } from '@/shared/types/integration';
import { actions } from '@/shared/types/permissions';
import { PageName } from '@/shared/types/segment';
import { RootState } from '@/shared/types/state';
import { getChannelKey } from '@/util/channelName';

import IntegrationContainer from './IntegrationContainer';
import IntegrationGroupItem from './IntegrationGroupItem';
import IntegrationItem from './IntegrationItem';
import IntegrationItemSkeleton from './IntegrationItemSkeleton';
import IntegrationsContextualHelp from './IntegrationsContextualHelp';

// Helper function to filter integrations by type
const typeFilter = (type: IntegrationType) => (integration: Integration) =>
  integration.type === type;

// Sort connected integrations by status (active first) and creation date (newest first)
const sortConnected = (integrations: Integration[]): Integration[] => {
  return [...integrations].sort((a, b) => {
    // Primary sort: status (active integrations first)
    if (a.active !== b.active) {
      return a.active ? -1 : 1;
    }

    // Secondary sort: creation date (newest first)
    const dateA = new Date(a.created).getTime();
    const dateB = new Date(b.created).getTime();
    return dateB - dateA;
  });
};

/**
 * IntegrationsPage - Main page component for managing integrations
 *
 * Features:
 * - Displays connected integrations with global sorting (active first, disabled last)
 * - Supports multiple connections of the same type with numbering
 * - Shows available integrations for connection
 * - Categorizes integrations into channels and communication platforms
 *
 * @returns {React.JSX.Element} The integrations page component
 */
const IntegrationsPage = (): React.JSX.Element => {
  const { t } = useTranslation();
  const deskId = useDeskId();
  const { desk } = useDesks(deskId);
  const { integrations, listStatus } = useIntegrations(deskId);
  const availableChannels = useSelector(selectAvailableChannels);
  const categorizedIntegrations = useSelector(selectCategorizedIntegrations);
  const canWrite = useSelector((state: RootState) =>
    getPermissions(state, 'integrations', actions.WRITE)
  );

  useTitle(t('pages.connections', { 0: desk?.name ?? '-' }));

  useSegments(PageName.INTEGRATIONS);

  // Get and sort connected integrations
  const connected = useMemo(() => {
    const connected =
      integrations?.filter((integration) => availableChannels.includes(integration.type)) || [];

    return sortConnected(connected);
  }, [integrations, availableChannels]);

  // Get available integrations
  const available = (Object.keys(integrationByType) as IntegrationType[]).filter((type) => {
    if (!availableChannels.includes(type)) {
      return false;
    }
    return !integrations?.find(typeFilter(type));
  });

  // Get categorized integrations from selector (already filtered by feature flags)
  const channels = categorizedIntegrations.channels.filter((type) => available.includes(type));
  const communicationPlatforms = categorizedIntegrations.communicationPlatforms.filter((type) =>
    available.includes(type)
  );

  // Render skeleton items
  const renderSkeletons = useCallback(
    (count: number) => (
      <IntegrationContainer>
        {Array(count)
          .fill(0)
          .map((_, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <IntegrationItemSkeleton key={i} />
          ))}
      </IntegrationContainer>
    ),
    []
  );

  // Render connected integrations
  const renderConnected = useCallback(() => {
    if (listStatus === 'pending') {
      return renderSkeletons(3);
    }
    if (connected.length === 0) {
      return null;
    }

    // Types of integrations that are grouped (currently only email is grouped)
    // Integration types included in this list will be grouped together in a single item
    const groupedIntegrationTypes: IntegrationType[] = [IntegrationType.EMAIL];

    const ungroupedIntegrations: Integration[] = [];

    // Map of integrations by type
    const integrationsByTypeMap = new Map<IntegrationType, Integration[]>();

    connected.forEach((integration) => {
      integrationsByTypeMap.set(integration.type, [
        ...(integrationsByTypeMap.get(integration.type) || []),
        integration,
      ]);

      // If the integration type is not in the grouped integration types list
      // add it to the ungrouped integrations list
      if (!groupedIntegrationTypes.includes(integration.type)) {
        ungroupedIntegrations.push(integration);
      }
    });

    return (
      <IntegrationContainer>
        {/* Show grouped email item if email integrations exist */}
        {integrationsByTypeMap.get(IntegrationType.EMAIL)?.length > 0 &&
          (() => {
            const config = getIntegrationByType(IntegrationType.EMAIL);
            if (!config) {
              return null;
            }

            return (
              <IntegrationGroupItem
                key="email-group"
                type={IntegrationType.EMAIL}
                name={t('email.title')}
                Icon={config.Icon}
                count={integrationsByTypeMap.get(IntegrationType.EMAIL)?.length}
              />
            );
          })()}

        {/* Show all other integrations with numbering for duplicates */}
        {ungroupedIntegrations.map((integration) => {
          const config = getIntegrationByType(integration.type);
          if (!config) {
            return null;
          }

          const sameTypeIntegrations = integrationsByTypeMap.get(integration.type);
          const hasCustomName = integration.name !== integration.type;
          const baseName = hasCustomName ? integration.name : t(getChannelKey(integration.type));
          const displayName =
            !hasCustomName && sameTypeIntegrations.length > 1
              ? `${baseName} (${sameTypeIntegrations.indexOf(integration) + 1})`
              : baseName;

          return (
            <IntegrationItem
              key={integration.integration_id}
              type={integration.type}
              name={displayName}
              Icon={config.Icon}
              connected={integration}
            />
          );
        })}
      </IntegrationContainer>
    );
  }, [connected, listStatus, t, renderSkeletons]);

  // Render available integrations
  const renderAvailableIntegrations = useCallback(
    (integrationTypes: IntegrationType[]) => {
      if (listStatus === 'pending') {
        return renderSkeletons(3);
      }
      if (integrationTypes.length === 0) {
        return null;
      }

      return (
        <IntegrationContainer>
          {integrationTypes.map((type: IntegrationType) => {
            const config = getIntegrationByType(type);
            if (!config) {
              return null;
            }

            // Use IntegrationGroupItem for email type (multi-instance integration)
            if (type === IntegrationType.EMAIL) {
              return (
                <IntegrationGroupItem
                  key={type}
                  type={type}
                  name={t(getChannelKey(type))}
                  Icon={config.Icon}
                />
              );
            }

            return (
              <IntegrationItem
                key={type}
                type={type}
                name={t(getChannelKey(type))}
                Icon={config.Icon}
              />
            );
          })}
        </IntegrationContainer>
      );
    },
    [listStatus, t, renderSkeletons]
  );

  return (
    <>
      <IntegrationsHeader />
      <PageContentWrapper readOnly={!canWrite} className="p-6">
        <div className="mx-auto mb-28 flex max-w-lg min-w-sm flex-col">
          {/* Contextual Help */}
          <IntegrationsContextualHelp
            title={t('connections.contextual_help.title')}
            name="about_integrations"
          >
            {t('connections.contextual_help.message')}
          </IntegrationsContextualHelp>

          {/* Connected */}
          {connected.length > 0 && (
            <div className="align-start mb-12 flex flex-col gap-3 self-stretch">
              <h2 className="px-1 text-lg leading-none font-semibold">
                {t('connections.connected')}
              </h2>
              {renderConnected()}
            </div>
          )}

          {/* Available */}
          {(channels.length > 0 || communicationPlatforms.length > 0) && (
            <div className="align-start flex flex-col self-stretch">
              <h2 className="mb-4 px-1 text-lg leading-none font-semibold">
                {t('connections.available')}
              </h2>

              {/* Channels */}
              {channels.length > 0 && (
                <>
                  <div className="flex items-center gap-1 px-1">
                    <InfoTooltip content={t('connections.channels_description')} side="top">
                      <div className="flex cursor-default items-center gap-1">
                        <h3 className="text-sm leading-5 font-normal text-muted-foreground">
                          {t('connections.channels')}
                        </h3>
                        <InfoIcon className="h-4 w-4 text-muted-foreground" />
                      </div>
                    </InfoTooltip>
                  </div>
                  <div className="mt-3 mb-4">{renderAvailableIntegrations(channels)}</div>
                </>
              )}

              {/* Communication Platforms */}
              {communicationPlatforms.length > 0 && (
                <>
                  <div className="flex items-center gap-1 px-1">
                    <InfoTooltip
                      content={t('connections.communication_platforms_description')}
                      side="top"
                    >
                      <div className="flex cursor-default items-center gap-1">
                        <h3 className="text-sm leading-5 font-normal text-muted-foreground">
                          {t('connections.communication_platforms')}
                        </h3>
                        <InfoIcon className="h-4 w-4 text-muted-foreground" />
                      </div>
                    </InfoTooltip>
                  </div>
                  <div className="mt-3 mb-4">
                    {renderAvailableIntegrations(communicationPlatforms)}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Request Connection */}
          <a
            href="mailto:support@moveo.ai"
            className="flex items-center gap-2 self-start px-1 hover:underline"
          >
            <span className="text-sm leading-5 font-medium text-foreground">
              {t('connections.request_connection')}
            </span>
            <ExternalLinkIcon className="h-4 w-4 text-muted-foreground" />
          </a>
        </div>
      </PageContentWrapper>
    </>
  );
};

export default IntegrationsPage;
